import React, { useRef, useState, useEffect, useCallback } from 'react';
import {
    Box, Paper, Typography, Button, IconButton, TextField, Alert, CircularProgress, Divider,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    History as HistoryIcon,
} from '@mui/icons-material';
import { apiFetch } from '../../api/apiFetch';
import { apiErrorMessage } from '../../api/apiError';
import MsaResults from '../msaResults/MsaResults';
import { AcceptanceBadge } from '../msaResults/msaFormat';

// ---------------------------------------------------------------------------
// Previous Studies — this module's past studies (filtered by studyType). Click a
// row to reload its stored results (result_detail) into the results area.
// ---------------------------------------------------------------------------
function PreviousStudies({ studies, loading, selectedId, onSelect }) {
    const headCellSx = { color: 'rgba(255,255,255,0.5)', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', borderBottom: '1px solid rgba(255,255,255,0.1)', py: 0.75, px: 1.5 };
    return (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <HistoryIcon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.4)' }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.7)' }}>
                    Previous Studies
                </Typography>
                {loading && <CircularProgress size={13} sx={{ color: 'rgba(255,255,255,0.3)' }} />}
            </Box>
            <TableContainer component={Paper} sx={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            {['Date', 'Description', 'Status'].map(h => (
                                <TableCell key={h} sx={headCellSx}>{h}</TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {studies.map((s) => (
                            <TableRow
                                key={s.id}
                                hover
                                onClick={() => onSelect(s)}
                                sx={{ cursor: 'pointer', backgroundColor: s.id === selectedId ? 'rgba(144,202,249,0.08)' : 'transparent', '&:hover': { backgroundColor: 'rgba(144,202,249,0.06)' } }}
                            >
                                <TableCell sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.75rem', py: 0.75, px: 1.5, whiteSpace: 'nowrap' }}>
                                    {s.created_at ? new Date(s.created_at).toLocaleDateString() : '—'}
                                </TableCell>
                                <TableCell sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', py: 0.75, px: 1.5, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    <Tooltip title={s.description || ''} placement="top-start" arrow><span>{s.description || '—'}</span></Tooltip>
                                </TableCell>
                                <TableCell sx={{ py: 0.75, px: 1.5 }}>
                                    <AcceptanceBadge status={s.acceptance_status} size="small" />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// MSAModulePage — shared chrome for the non-GRR MSA module entry pages.
//
// One wrapper, four configs. It owns:
//   - the required Study Description field,
//   - the Run Study lifecycle: builds the payload from the child input component
//     (via an imperative `buildInputs()` ref), POSTs `{ studyType, description,
//     ...inputs }` to /api/msa through the shared apiFetch wrapper, and manages
//     loading / error / warning state,
//   - the results area, delegating rendering to <MsaResults studyType results/>.
//
// The module-specific input UI is supplied as a FUNCTION child:
//     <MSAModulePage studyType="capabilityImpact" title="…" onBack={onBack}>
//        {({ inputRef, disabled }) => <CapabilityImpactInputs ref={inputRef} disabled={disabled} />}
//     </MSAModulePage>
//
// The input component must be a forwardRef exposing:
//     useImperativeHandle(ref, () => ({ buildInputs() {…} }))
//   where buildInputs() returns the module-specific payload fields, or THROWS an
//   Error(message) for client-side validation (the message is surfaced inline).
// ---------------------------------------------------------------------------

const inputSx = {
    '& .MuiOutlinedInput-root': {
        color: 'rgba(255, 255, 255, 0.9)',
        '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.23)' },
        '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
        '&.Mui-focused fieldset': { borderColor: 'rgba(144, 202, 249, 0.7)' },
    },
    '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.6)' },
    '& .MuiInputLabel-root.Mui-focused': { color: 'rgba(144, 202, 249, 0.9)' },
};

function MSAModulePage({ studyType, title, subtitle, onBack, descriptionPlaceholder, helperText, children }) {
    const inputRef = useRef(null);

    const [description, setDescription] = useState('');
    const [submitting, setSubmitting]   = useState(false);
    const [error, setError]             = useState(null);
    const [warnings, setWarnings]       = useState([]);
    const [studyResults, setStudyResults] = useState(null); // { id, createdAt, description, results }
    const [history, setHistory]           = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);

    // Fetch this module's past studies (filtered by studyType) for reload.
    const fetchHistory = useCallback(async () => {
        setHistoryLoading(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/msa-studies`, { credentials: 'include' });
            if (res.ok) {
                const data = await res.json();
                setHistory((data.studies || []).filter(s => s.study_type === studyType));
            }
        } catch (e) {
            // history is non-critical; ignore fetch failures
        } finally {
            setHistoryLoading(false);
        }
    }, [studyType]);

    useEffect(() => { fetchHistory(); }, [fetchHistory]);

    // Reload a past study: show its stored results AND re-populate the module inputs
    // so the user can tweak + re-run. The persisted `measurements` is the raw engine
    // input (carries categories / spec limits), so it doubles as the result `meta`.
    const handleSelectHistory = useCallback((row) => {
        const persistedInput = row.measurements && typeof row.measurements === 'object' ? row.measurements : null;
        setStudyResults({
            id:          row.id,
            createdAt:   row.created_at,
            description: row.description,
            results:     row.result_detail,
            meta:        persistedInput,
        });
        setDescription(row.description || '');
        if (persistedInput) inputRef.current?.hydrate?.(persistedInput);
        setError(null);
    }, []);

    const handleSubmit = async () => {
        setError(null);
        setWarnings([]);

        if (!description.trim()) {
            setError('Enter a study description.');
            return;
        }

        // Ask the module input component to assemble + client-validate its inputs.
        let inputs;
        try {
            inputs = inputRef.current?.buildInputs?.();
        } catch (e) {
            setError(e?.message || 'Please complete the study inputs.');
            return;
        }
        if (!inputs) {
            setError('Please complete the study inputs.');
            return;
        }

        setSubmitting(true);
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
            const res = await apiFetch(`${backendUrl}/api/msa`, {
                method:      'POST',
                headers:     { 'Content-Type': 'application/json' },
                credentials: 'include',
                body:        JSON.stringify({ studyType, description: description.trim(), ...inputs }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(apiErrorMessage(res.status, data, 'Study failed. Check inputs and retry.'));
            } else {
                setStudyResults({
                    id:          data.id,
                    createdAt:   data.createdAt,
                    description: data.description,
                    results:     data.results,
                    meta:        inputs, // the submitted inputs (categories, spec limits, …) for result rendering
                });
                if (data.warnings?.length) setWarnings(data.warnings);
                fetchHistory(); // refresh the Previous Studies list with the new study
            }
        } catch (e) {
            setError('Network error. Check your connection and retry.');
        } finally {
            setSubmitting(false);
        }
    };

    const hasResults = Boolean(studyResults?.results);

    return (
        <Box
            component="main"
            sx={{
                flex: 1,
                overflow: 'auto',
                p: { xs: 1.5, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                width: '100%',
            }}
        >
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <IconButton
                    size="small"
                    onClick={onBack}
                    title="Back to study selector"
                    sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: '#90caf9' } }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Box sx={{ mr: 'auto' }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem', lineHeight: 1.2 }}>
                        {title}
                    </Typography>
                    {subtitle && (
                        <Typography variant="caption" sx={{ color: '#90caf9' }}>
                            {subtitle}
                        </Typography>
                    )}
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleSubmit}
                    disabled={submitting}
                    startIcon={submitting ? <CircularProgress size={12} color="inherit" /> : <SendIcon />}
                    sx={{ py: 0.5, px: 2 }}
                >
                    {submitting ? 'Running…' : 'Run Study'}
                </Button>
            </Box>

            {/* Input card */}
            <Paper sx={{ p: { xs: 1.5, md: 2.5 }, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                    label="Study Description"
                    required
                    size="small"
                    fullWidth
                    sx={inputSx}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder={descriptionPlaceholder}
                    disabled={submitting}
                    inputProps={{ maxLength: 500 }}
                />
                {helperText && (
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.45)', lineHeight: 1.5, mt: -1 }}>
                        {helperText}
                    </Typography>
                )}

                <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />

                {/* Module-specific input UI (function-as-child, wired to inputRef) */}
                {typeof children === 'function'
                    ? children({ inputRef, disabled: submitting })
                    : children}

                {error && (
                    <Alert severity="error" sx={{ py: 0.5 }} onClose={() => setError(null)}>
                        {error}
                    </Alert>
                )}
                {warnings.length > 0 && (
                    <Alert severity="warning" sx={{ py: 0.5 }} onClose={() => setWarnings([])}>
                        {warnings.join('; ')}
                    </Alert>
                )}
            </Paper>

            {/* Results */}
            {hasResults ? (
                <Paper sx={{ p: { xs: 1.5, md: 2.5 }, backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                            {studyResults.description}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                            {studyResults.createdAt ? new Date(studyResults.createdAt).toLocaleString() : ''}
                        </Typography>
                    </Box>
                    <MsaResults studyType={studyType} results={studyResults.results} meta={studyResults.meta} />
                </Paper>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, opacity: 0.5, py: 6 }}>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', textAlign: 'center', maxWidth: 380 }}>
                        Complete the inputs above and press <strong>Run Study</strong> to see results.
                    </Typography>
                </Box>
            )}

            {/* Previous studies for this module (click a row to reload its results) */}
            {history.length > 0 && (
                <>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)' }} />
                    <PreviousStudies
                        studies={history}
                        loading={historyLoading}
                        selectedId={studyResults?.id}
                        onSelect={handleSelectHistory}
                    />
                </>
            )}
        </Box>
    );
}

export default MSAModulePage;
